import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./ReservationSchedulerNew.css";
import EditorModal from "../scheduler/EditorModal";
import { minToHrs } from "../scheduler/Utilities";
import { AppContext } from "../../../context/AppContext";
import Utils from "../../../utils/Utils";
import { toast } from "react-toastify";
import PortalMenu from "./PortalMenu";
import ScheduleLoader from "./scheduleLoader";
import UnlistedReservations from "./UnlistedReservations";
import TableList from "./TablesList";
import * as Hi from "react-icons/hi";
import DateNavBar from "./DateNavBar";
import ConfrimPopup from "./ConfrimPopup";
import StatusLegend from "./StatusLegend";
import ClosureDrawer from "./ClosureDrawer";
import {
  pad,
  ymd,
  fmtHM,
  timeToMins,
  hhmmssToMins,
  fmtHourLabelInto24,
  minsToTime,
  parseYmdHmsToLocal,
  dateToMinutes,
  rid,
  norm,
  formatTo12Hour,
  isTodayOrFuture,
} from "./SchedulerHelpers";
import { IoIosCheckmarkCircle } from "react-icons/io";
import {
  STATUS_COLOURS,
  getStatusColour,
} from "../../../constants/ReservationStatus";

/* ---------- Config (keep in sync with CSS --rowH / --headH) ---------- */
const ROW_H = 40; // px per row
const HEAD_H = 76; // px top tick strip (matches .rs-left--head)

export default function ReservationSchedulerNew({
  startHour = 0,
  endHour = 24,
  pxPerMin: pxPerMinProp = 2,
  snapMins = 15,
  initialEvents = [],
  disabledHours = [],
}) {
  const {
    upcomingEnquiries,
    todayEnquiries,
    tokenData,
    updateEnquiry,
    fetchNewReservations,
    fetchFutureReservations,
    reservationSettings,
    todaysReservationList,
    addNewReservartion,
    upcomingwithtodaysReservationList,
    upcomingwithTodays,
    fetchAllTables,
    tableLists,
    reservationLoading,
    upComingHolidays,
    reservFoodpageTiming,
    closureList,
    reservationStatus,
    foodpageReservSettings,
    updateSettings,
    getSettings,
    reservSettingsLoading,
  } = useContext(AppContext);
  const viewportRef = useRef(null);
  const rowTapRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(ymd(new Date()));
  const [events, setEvents] = useState(() =>
    initialEvents.map((e) => ({ id: e.id || rid(), ...e })),
  );
  const [editing, setEditing] = useState(null);
  const [showConfrim, setShowConfirm] = useState(false);
  const [eventId, setEventId] = useState(0);
  const [showClosureModal, setShowClosureModal] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [selectedClosure, setSelectedClosure] = useState(null);
  const [isCompact, setIsCompact] = useState(false);

  // Shrink timeline density on small screens so the canvas is less huge
  const pxPerMin = isCompact ? Math.min(pxPerMinProp, 1.25) : pxPerMinProp;

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const media = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsCompact(media.matches);

    apply();
    if (media.addEventListener) {
      media.addEventListener("change", apply);
      return () => media.removeEventListener("change", apply);
    }
    media.addListener(apply);
    return () => media.removeListener(apply);
  }, []);

  useEffect(() => {
    return () => {
      const tap = rowTapRef.current;
      if (!tap) return;
      if (tap.onMove) {
        window.removeEventListener("pointermove", tap.onMove);
        window.removeEventListener("touchmove", tap.onMove);
      }
      if (tap.onEnd) {
        window.removeEventListener("pointerup", tap.onEnd);
        window.removeEventListener("pointercancel", tap.onEnd);
        window.removeEventListener("touchend", tap.onEnd);
        window.removeEventListener("touchcancel", tap.onEnd);
      }
      rowTapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const currentLSDate = sessionStorage.getItem("selectedDate");

    if (!currentLSDate) {
      sessionStorage.setItem("selectedDate", selectedDate);
      return;
    }

    setSelectedDate(currentLSDate);
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    sessionStorage.setItem("selectedDate", selectedDate);
  }, [selectedDate]);

  const tables = useMemo(() => {
    if (!Array.isArray(tableLists)) return [];

    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    });

    return tableLists
      .filter(
        (t) =>
          t?.status?.toLowerCase() === "active" &&
          (!t.tableType || t.tableType === "dining"),
      )
      .map((t) => ({
        id: norm(t.id),
        tableID: t?.tableId,
        name: t.name || `Table ${t.tableID ?? t.id}`,
        chairs: Number.parseInt(t.chairs, 10) || 0,
        raw: t,
      }))
      .sort((a, b) =>
        collator.compare(a.raw?.tableID ?? a.name, b.raw?.tableID ?? b.name),
      );
  }, [tableLists]);

  const holidayIntervals = useMemo(() => {
    if (!upComingHolidays || upComingHolidays.length === 0) return;
    const days = [];
    upComingHolidays
      .filter((h) => h.status === "Active")
      .map((h) => {
        const start = new Date(h.startTime.replace(" ", "T"));
        const end = new Date(h.endTime.replace(" ", "T"));

        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        days.push({ start, end });
      });

    return days;
    //! this is for highlight days
    // const days = [];
    // upComingHolidays.forEach((h) => {
    //   const start = new Date(h.startTime.replace(" ", "T"));
    //   const end = new Date(h.endTime.replace(" ", "T"));
    //   start.setHours(0, 0, 0, 0);
    //   end.setHours(0, 0, 0, 0);

    //   for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    //     days.push(new Date(d));
    //   }
    // });
    // return days;
  }, [upComingHolidays]);

  // const totalMins = (endHour - startHour) * 60; // 1440 for 24h
  // const canvasW = totalMins * pxPerMin;

  const TAIL_HOUR_W = 60 * pxPerMin; // one extra hour
  const totalMins = (endHour - startHour) * 60;
  const canvasW = totalMins * pxPerMin + TAIL_HOUR_W;
  // Exact match to left TableList height (head + one row per table)
  const vpHeight = HEAD_H + tables.length * ROW_H;

  const [dayEvents, setDayEvents] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [menuFor, setMenuFor] = useState(null);
  const [menuPos, setMenuPos] = useState({
    x: 0,
    y: 0,
    placement: "bottom-end",
  });

  const effectiveSnap = 15;
  const windowStartMin = startHour * 60;
  const windowEndMin = endHour * 60;

  // const dayEvents = useMemo(
  //   () => events.filter((e) => e.date === selectedDate),
  //   [events, selectedDate]
  // );

  const rowEvents = (t) => dayEvents.filter((bk) => norm(bk.tableId) === t.id);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setMenuFor(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const rootRef = useRef(null);
  const onRootClick = useCallback((e) => {
    if (!e.target.closest(".ev-menu") && !e.target.closest(".ev-menu-btn")) {
      setMenuFor(null);
    }
  }, []);

  const parseJSONSafe = (s, fallback = []) => {
    try {
      return s ? JSON.parse(s) : fallback;
    } catch {
      return fallback;
    }
  };

  // To convert the date into day
  const dayKeyOf = (isoYmd) =>
    [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ][new Date(isoYmd).getDay()];

  // "HH:MM" -> minutes since midnight
  const hhmmToMins = (s) => {
    const [h, m] = (s || "00:00").split(":").map(Number);
    return h * 60 + (m || 0);
  };

  function dayKeyFromDate(d) {
    const i = new Date(d).getDay();
    return [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ][i];
  }

  function getOpenRangesForDate(startHour, endHour) {
    if (!reservFoodpageTiming) return [];

    const key = dayKeyFromDate(selectedDate);
    const slots = Array.isArray(reservFoodpageTiming[key])
      ? reservFoodpageTiming[key]
      : [];

    const dayRanges = [];

    if (slots.length === 0) {
      return [];
    }

    for (const slot of slots) {
      if (!slot) continue;

      if (slot.status && slot.status.toLowerCase() !== "active") {
        continue;
      }

      const a = hhmmssToMins(slot.openingTime);
      const b = hhmmssToMins(slot.closingTime);

      if (!Number.isFinite(a) || !Number.isFinite(b)) continue;

      if (b > a) {
        dayRanges.push({ a, b });
      } else if (b < a) {
        dayRanges.push({ a, b: 24 * 60 });
        dayRanges.push({ a: 0, b });
      }
    }

    const windowA = startHour * 60;
    const windowB = endHour * 60;

    const clamped = dayRanges
      .map(({ a, b }) => ({
        a: Math.max(windowA, a),
        b: Math.min(windowB, b),
      }))
      .filter((r) => r.b > r.a);

    clamped.sort((x, y) => x.a - y.a);

    const merged = [];
    for (const r of clamped) {
      const last = merged[merged.length - 1];
      if (last && r.a <= last.b) {
        last.b = Math.max(last.b, r.b);
      } else {
        merged.push({ ...r });
      }
    }

    return merged;
  }

  // Scroll to first available window (or start if none)
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp || !reservFoodpageTiming) return;

    const opens = getOpenRangesForDate(startHour, endHour);

    if (!opens || opens.length === 0) {
      vp.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }

    const MIN_BLOCK = 30;
    const first = opens.find((r) => r.b - r.a >= MIN_BLOCK) || opens[0];

    const x = Math.max(0, (first.a - startHour * 60) * pxPerMin - 40);

    vp.scrollTo({ left: x, behavior: "smooth" });
  }, [selectedDate, reservFoodpageTiming, pxPerMin, startHour, endHour]);

  // Build disabled ranges as the complement of open windows within the visible window
  const buildDisabledFromOpen = (openWindows, winStartMin, winEndMin) => {
    const open = (openWindows || [])
      .map(({ start, end }) => ({
        a: Math.max(winStartMin, hhmmToMins(start)),
        b: Math.min(winEndMin, hhmmToMins(end)),
      }))
      .filter(({ a, b }) => b > a)
      .sort((x, y) => x.a - y.a);

    if (open.length === 0) return [{ a: winStartMin, b: winEndMin }];

    // merge overlaps
    const merged = [];
    for (const seg of open) {
      const last = merged[merged.length - 1];
      if (!last || seg.a > last.b) merged.push({ ...seg });
      else last.b = Math.max(last.b, seg.b);
    }

    // complement
    const disabled = [];
    let cur = winStartMin;
    for (const seg of merged) {
      if (seg.a > cur) disabled.push({ a: cur, b: seg.a });
      cur = Math.max(cur, seg.b);
    }
    if (cur < winEndMin) disabled.push({ a: cur, b: winEndMin });
    return disabled;
  };

  const closureRangesForDay = () => {
    if (!selectedDate || !closureList) return [];

    const dayStart = new Date(selectedDate);
    dayStart.setHours(0, 0, 0, 0);

    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(23, 59, 59, 999);

    const disabled = [];

    for (const item of closureList) {
      if ((item.status || "").toLowerCase() !== "active") continue;

      const start = parseYmdHmsToLocal(item.startTime);
      const end = parseYmdHmsToLocal(item.endTime);
      if (!start || !end) continue;

      // overlap with this calendar day
      const overlapStartMs = Math.max(dayStart.getTime(), start.getTime());
      const overlapEndMs = Math.min(dayEnd.getTime(), end.getTime());
      if (overlapEndMs <= overlapStartMs) continue;

      const overlapStart = new Date(overlapStartMs);
      const overlapEnd = new Date(overlapEndMs);

      const a = dateToMinutes(overlapStart);
      const b = dateToMinutes(overlapEnd);

      if (b > a) disabled.push({ a, b });
    }

    return disabled;
  };

  const { scheduleDisabled, closureDisabled } = useMemo(() => {
    if (!reservFoodpageTiming || !selectedDate) {
      return { scheduleDisabled: [], closureDisabled: [] };
    }

    const dayKey = dayKeyOf(selectedDate);
    const slots = Array.isArray(reservFoodpageTiming?.[dayKey])
      ? reservFoodpageTiming[dayKey]
      : [];

    const openWindows = [];

    for (const s of slots) {
      if ((s?.status || "").toLowerCase() !== "active") continue;

      const startHHMM = (s.openingTime || "").slice(0, 5); // "HH:MM"
      const endHHMM = (s.closingTime || "").slice(0, 5);

      const a = hhmmToMins(startHHMM);
      const b = hhmmToMins(endHHMM);
      if (!Number.isFinite(a) || !Number.isFinite(b)) continue;

      if (b > a) {
        // simple window
        openWindows.push({ start: startHHMM, end: endHHMM });
      } else {
        // crosses midnight
        openWindows.push({ start: startHHMM, end: "24:00" });
        openWindows.push({ start: "00:00", end: endHHMM });
      }
    }

    // 1️⃣ normal “schedule” disabled ranges
    const scheduleDisabled = buildDisabledFromOpen(
      openWindows,
      windowStartMin,
      windowEndMin,
    );

    // 2️⃣ closure disabled ranges
    const closureDisabled = closureRangesForDay(windowStartMin, windowEndMin);

    return { scheduleDisabled, closureDisabled };
  }, [
    reservFoodpageTiming,
    closureList,
    selectedDate,
    windowStartMin,
    windowEndMin,
  ]);

  // choose the first placement that fits inside the viewport
  function pickPlacement(btnRect, menuW, menuH, padding = 8, gap = 6) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const fits = (x, y) =>
      x >= padding &&
      y >= padding &&
      x + menuW <= vw - padding &&
      y + menuH <= vh - padding;

    const candidates = [
      {
        name: "bottom-end",
        x: Math.min(btnRect.right - menuW, vw - menuW - padding),
        y: btnRect.bottom + gap,
      },
      {
        name: "top-end",
        x: Math.min(btnRect.right - menuW, vw - menuW - padding),
        y: btnRect.top - menuH - gap,
      },
      {
        name: "right-start",
        x: btnRect.right + gap,
        y: Math.max(btnRect.top, padding),
      },
      {
        name: "left-start",
        x: btnRect.left - menuW - gap,
        y: Math.max(btnRect.top, padding),
      },
    ];

    for (const c of candidates) {
      if (fits(c.x, c.y)) return c;
    }

    const fallback = {
      name: "clamped",
      x: Math.min(
        Math.max(btnRect.right - menuW, padding),
        vw - menuW - padding,
      ),
      y: Math.min(
        Math.max(btnRect.bottom + gap, padding),
        vh - menuH - padding,
      ),
    };
    return fallback;
  }

  const fetchDateEvents = () => {
    if (!Array.isArray(upcomingwithTodays) || !selectedDate) {
      setDayEvents([]);
      return;
    }

    // helper: normalise table id to a string (consistent with your left list keys)
    const normId = (v) => (v === 0 || v ? String(v) : "");

    const reservationsForDay = upcomingwithTodays.filter((x) => {
      if (!x?.bookingTime) return false;
      // bookingTime ex: "2025-11-05 16:00:00"
      const dt = new Date(String(x.bookingTime).replace(" ", "T"));
      return dt.toLocaleDateString("en-CA") === selectedDate;
    });

    const details = [];

    for (const x of reservationsForDay) {
      const c = getStatusColour(x.status);

      // Extract start/end minutes from bookingTime
      const bt = String(x.bookingTime);
      const timePart = bt.includes(" ") ? bt.split(" ")[1] : bt; // "HH:MM(:SS)"
      if (!timePart) continue;

      const hhmm = timePart.slice(0, 5); // "HH:MM"
      const startMins = timeToMins(hhmm);
      const endMins = startMins + 120; // default 2 hours

      // Base event payload (without table binding)
      const base = {
        ...x,
        date: ymd(new Date(x.bookingTime)),
        start: startMins,
        end: endMins,
        name: x.name || "Guest",
        colour: c.text,
        textColour: c.bg,
        bookingDate: selectedDate,
        status: x.status || null,
      };
      const reservedTables = JSON.parse(x?.tables);
      const tablesArr =
        reservedTables && Array.isArray(reservedTables) ? reservedTables : [];

      if (tablesArr.length > 0) {
        for (const tid of tablesArr) {
          const tableId = normId(tid);
          details.push({
            ...base,
            tableId,
            id: x.id,
          });
        }
      } else {
        // Unallocated item (no table yet) – keep original id
        details.push({
          ...base,
          tableId: "", // or undefined/null if your code treats that as unallocated
          id: x.id,
        });
      }
    }

    setDayEvents(details);
  };

  useEffect(() => {
    if (!upcomingwithTodays || upcomingwithTodays.length == 0) return;
    fetchDateEvents();
  }, [upcomingwithTodays, selectedDate]);

  const nearestSnap = (mins) => {
    // Math.max(0, Math.min(totalMins, Math.round(mins / snapMins) * snapMins));
    // const snapped = Math.floor(mins / snapMins) * snapMins;
    // return Math.max(0, Math.min(totalMins, snapped));

    const snapped = Math.floor(mins / effectiveSnap) * effectiveSnap;
    return Math.max(0, Math.min((endHour - startHour) * 60, snapped));
  };

  function xToMinutes(clientX) {
    const el = viewportRef.current;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left + el.scrollLeft;
    return nearestSnap(x / pxPerMin); // minutes from scheduler start
  }

  const inDisabled = (absMins) =>
    scheduleDisabled.some(({ a, b }) => absMins >= a && absMins < b);

  const inDisabledClosure = (absMins) =>
    closureDisabled.some(({ a, b }) => absMins >= a && absMins < b);

  function xToMinutes(clientX) {
    const el = viewportRef.current;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left + el.scrollLeft;
    return nearestSnap(x / pxPerMin);
  }

  const ROW_TAP_THRESHOLD_PX = 10;

  function clearRowTapListeners() {
    const tap = rowTapRef.current;
    if (!tap) return;
    if (tap.onMove) {
      window.removeEventListener("pointermove", tap.onMove);
      window.removeEventListener("touchmove", tap.onMove);
    }
    if (tap.onEnd) {
      window.removeEventListener("pointerup", tap.onEnd);
      window.removeEventListener("pointercancel", tap.onEnd);
      window.removeEventListener("touchend", tap.onEnd);
      window.removeEventListener("touchcancel", tap.onEnd);
    }
  }

  function openSlotAt(tableId, clientX) {
    if (updateLoading || reservationLoading) return;

    const minsFromStart = xToMinutes(clientX);
    const absStart = startHour * 60 + minsFromStart;
    if (inDisabled(absStart)) return;
    if (inDisabledClosure(absStart)) return;

    const start = absStart;
    const end = Math.min(start + 30, startHour * 60 + totalMins);

    const existing = dayEvents.find(
      (bk) => bk.tableId === tableId && start < bk.end && end > bk.start,
    );

    if (existing) {
      setEditing(existing);
      return;
    }

    const bookingTime = Utils.minsToHMS(start);

    setEditing({
      id: undefined,
      tableId,
      date: selectedDate,
      start,
      end,
      name: "",
      chairs: 2,
      bookingTime,
      bookingDate: selectedDate,
    });
  }

  function onRowPointerDown(tableId, e) {
    if (updateLoading || reservationLoading) return;
    if (e.button != null && e.button !== 0) return;

    // Don't open on touch-start — wait for a real tap after pointerup.
    // Track movement on window so viewport scrolling still cancels the tap.
    clearRowTapListeners();

    const onMove = (ev) => {
      const tap = rowTapRef.current;
      if (!tap || tap.moved) return;
      const x = ev.clientX ?? ev.touches?.[0]?.clientX;
      const y = ev.clientY ?? ev.touches?.[0]?.clientY;
      if (x == null || y == null) return;
      if (
        Math.abs(x - tap.x) > ROW_TAP_THRESHOLD_PX ||
        Math.abs(y - tap.y) > ROW_TAP_THRESHOLD_PX
      ) {
        tap.moved = true;
      }
    };

    const onEnd = (ev) => {
      const tap = rowTapRef.current;
      clearRowTapListeners();
      rowTapRef.current = null;
      if (!tap || tap.tableId !== tableId || tap.moved) return;

      const x =
        ev.clientX ??
        ev.changedTouches?.[0]?.clientX ??
        tap.x;
      openSlotAt(tableId, x);
    };

    rowTapRef.current = {
      tableId,
      x: e.clientX,
      y: e.clientY,
      moved: false,
      onMove,
      onEnd,
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("pointerup", onEnd, { passive: true });
    window.addEventListener("pointercancel", onEnd, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    window.addEventListener("touchcancel", onEnd, { passive: true });
  }

  function layoutRow(events) {
    // sort: earlier first; if same start, longer first
    const sorted = [...events].sort(
      (a, b) => a.start - b.start || b.end - a.end,
    );
    const lanesEnd = []; // lane i -> last end time in that lane
    const out = [];

    for (const ev of sorted) {
      // find the first lane where this event does NOT overlap
      let lane = 0;
      while (lane < lanesEnd.length && ev.start < lanesEnd[lane]) lane++;
      lanesEnd[lane] = ev.end; // place/update lane end
      out.push({ ...ev, lane });
    }
    return { items: out, laneCount: Math.max(1, lanesEnd.length) };
  }

  function saveBooking(bk) {
    if (bk.id) setEvents((prev) => prev.map((x) => (x.id === bk.id ? bk : x)));
    else setEvents((prev) => [...prev, { ...bk, id: rid() }]);
    setEditing(null);
  }
  function deleteBooking(id) {
    // setEvents((prev) => prev.filter((x) => x.id !== id));
    setEditing(null);
  }

  function isBookingTimeFormat(str, disallowZeroDate = true) {
    if (typeof str !== "string") return false;

    // Basic shape: 4-2-2 digits + space + 2:2:2 digits
    const re = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

    if (!re.test(str)) return false;
    if (disallowZeroDate && str === "0000-00-00 00:00:00") return false;

    return true;
  }

  const handleEnableReservations = async () => {
    const { updated_at, source, id, shopID, ...rest } = foodpageReservSettings;

    const payload = {
      ...rest,
      reservationStatus: "Enabled",
      source: "REACT",
    };

    await updateSettings(payload, {
      onSuccess: async (res) => {
        await getSettings();
      },
      onFailed: (err) => {
        console.log("updated error", err);
        throw new Error(err);
      },
    });
  };

  const handleUpdateStatus = async (data, updatedStatus) => {
    if (!menuFor || menuFor === 0 || menuFor === "0" || !data || !updatedStatus)
      return;

    if (menuFor != data?.id) return;

    try {
      setUpdateLoading(true);

      const {
        formattedID,
        start,
        end,
        id,
        date,
        tableId,
        colour,
        textColour,
        status,
        bookingTime,
        tables,
        addedTime,
        ...rest
      } = data;

      const tableData =
        tables && tables.length != 0 && typeof tables === "string"
          ? JSON.parse(tables)
          : tables;

      let splitTime = "";
      let bookedDate = "";

      if (bookingTime && bookingTime.includes(" ")) {
        const parts = bookingTime.split(" ");
        bookedDate = parts[0];
        splitTime = parts[1] || "";
      } else if (bookingTime && bookingTime.includes(":")) {
        splitTime = bookingTime;
      }

      const isBookingValid = isBookingTimeFormat(bookingTime);

      if (!isBookingValid) {
        toast.error("Booking time is invalid");
        return;
      }

      if (!splitTime || splitTime.length === 0) {
        toast.error("Please select a valid time.");
        return;
      }

      if (!bookedDate || bookedDate.length === 0) {
        toast.error("Please select a valid time.");
        return;
      }

      const bookingMerged = `${bookedDate} ${
        splitTime.length === 5 ? `${splitTime}:00` : splitTime
      }`;

      const siteStorageURL = localStorage.getItem("siteURL") || "";
      const siteURL = siteStorageURL
        ? `${siteStorageURL}/view-reservation?reserv=`
        : "";
      const shopToken = tokenData?.shopCustomToken;
      if (!shopToken) {
        toast.error("Authorisation token missing.");
        return;
      }

      //! validate upcoming date with status
      const isFuture = isTodayOrFuture(bookedDate);
      if (
        isFuture &&
        (updatedStatus === "Completed" || updatedStatus === "Seated")
      ) {
        toast.error(
          `Future reservations cannot be updated to ${updatedStatus}.`,
        );
        return;
      }

      const updatePayload = {
        ...rest,
        tables: tableData,
        bookingTime: splitTime.length === 5 ? `${splitTime}:00` : splitTime,
        status: updatedStatus,
        userID: 0,
        shopmessage: "",
        advancePayment: "",
        advanceAmount: "",
        amountStatus: "",
        paymentMethod: "",
        transactionID: "",
        baseUrl: siteURL,
        source: "NextJS",
      };

      const headers = { "x-shopToken": shopToken };

      await updateEnquiry(id, updatePayload, {
        onSuccess: async () => {
          toast.success("Reservation details updated successfully.");
          await fetchNewReservations(shopToken);
          await todaysReservationList(shopToken);
          await fetchFutureReservations(shopToken);
          await upcomingwithtodaysReservationList();
          fetchDateEvents();
          setMenuFor(null);
        },
        onFailed: (err) => {
          const msg = err?.messages ?? "Update failed. Please try again.";
          toast.error(msg);
        },
        headers,
      });
    } finally {
      setMenuFor(null);
      setUpdateLoading(false);
    }
  };

  const onClose = () => {
    setShowConfirm(false);
  };

  //! for holidays
  const isSameDayInInterval = (dateStr, intervals) => {
    if (!dateStr || !Array.isArray(closureList)) return false;

    const checkDate = new Date(dateStr + "T12:00:00");

    return closureList.some(({ startTime, endTime }) => {
      if (!startTime || !endTime) return false;

      const start = new Date(startTime);
      const end = new Date(endTime);

      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      return checkDate >= start && checkDate <= end;
    });
  };

  const isHolidaySelected = useMemo(() => {
    if (!selectedDate || !closureList || closureList.length == 0) return;
    return isSameDayInInterval(selectedDate, closureList);
  }, [selectedDate, closureList]);

  const oncloseClosureModal = () => {
    setShowClosureModal(false);
  };

  const fetchClosureData = () => {
    if (!closureList || closureList.length === 0 || !selectedDate) return;

    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);

    const match = closureList.find((item) => {
      if ((item.status || "").toLowerCase() !== "active") return false;

      const start = new Date(item.startTime.replace(" ", "T"));
      const end = new Date(item.endTime.replace(" ", "T"));

      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      return selected >= start && selected <= end;
    });

    setSelectedClosure(match || null);
  };

  const fetchClosureByDate = () => {
    if (!selectedDate || !closureList || closureList.length == 0) return;
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);

    const match = closureList.find((item) => {
      if ((item.status || "").toLowerCase() !== "active") return false;

      const start = new Date(item.startTime.replace(" ", "T"));
      const end = new Date(item.endTime.replace(" ", "T"));

      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      return selected >= start && selected <= end;
    });

    return match;
  };

  useEffect(() => {
    fetchClosureData();
  }, [closureList, selectedDate]);

  useEffect(() => {
    if (!selectedClosure) {
      setIsClosed(false);
    } else {
      setIsClosed(true);
    }
  }, [selectedClosure]);

  return (
    <>
      {showConfrim && (
        <ConfrimPopup
          close={onClose}
          eventId={eventId}
          setEventId={setEventId}
          showConfrim={showConfrim}
          setShowConfirm={setShowConfirm}
        />
      )}
      {reservationStatus === "Disabled" && (
        <div className="disabled_alert_box">
          <h2 className="disabled_alert_title">
            Reservations are disabled right now
          </h2>
          <p className="disabled_alert_subtitle">
            Customers won’t be able to place new bookings until you enable it.
          </p>

          <button
            type="button"
            className="disabled_alert_btn"
            onClick={handleEnableReservations}
            disabled={reservSettingsLoading}
          >
            {!reservSettingsLoading ? (
              <>
                {" "}
                <IoIosCheckmarkCircle />
                Enable Reservations
              </>
            ) : (
              <>
                <div
                  class="spinner-border spinner-border-sm text-light"
                  role="status"
                ></div>{" "}
                <span>Please wait!</span>
              </>
            )}
          </button>
        </div>
      )}

      <div className="rs-layout">
        <div className="rs-root" ref={rootRef} onMouseDown={onRootClick}>
          <DateNavBar
            count={dayEvents?.length ?? 0}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            holidayIntervals={holidayIntervals}
            isClosed={isClosed}
            setShowClosureModal={setShowClosureModal}
            setIsClosed={setIsClosed}
            selectedClosure={selectedClosure}
          />
          {isHolidaySelected && selectedClosure && (
            <div className="rs-holiday-banner">
              {`Reservations are closed on ${Utils.formatDate(
                selectedDate,
              )} due to a closure from ${
                selectedClosure
                  ? `${formatTo12Hour(
                      selectedClosure?.startTime,
                    )} to ${formatTo12Hour(selectedClosure?.endTime)}`
                  : ""
              }`}
            </div>
          )}

          <div className="position-relative">
            <div className="rs-scheduler-wrapper">
              {reservationStatus !== "Disabled" &&
                upcomingwithTodays &&
                dayEvents &&
                dayEvents.length != 0 && (
                  <UnlistedReservations
                    tables={tables}
                    reservations={upcomingwithTodays}
                    selectedDate={selectedDate}
                    fetchDateEvents={fetchDateEvents}
                    show={!!editing}
                    editing={editing}
                    onClose={() => {
                      setEditing(null);
                    }}
                    onSave={saveBooking}
                    onDelete={deleteBooking}
                    setEditing={setEditing}
                  />
                )}
              <div
                //! for disable the scheduler on holiday
                className={
                  reservationStatus === "Disabled"
                    ? "rs-grid overlay_disabeld"
                    : "rs-grid"
                }
                // className="rs-grid"
              >
                {/* Left list */}
                {/* <div className="rs-left-list">
                  <div className="rs-left rs-left--head">Table</div>
                  {tables.map((t) => (
                    <div key={t.id} className="rs-left">
                      {t.name}
                    </div>
                  ))}
                </div> */}
                <TableList tables={tables} />

                {/* Single timeline space (ticks + rows in one scroller) */}
                <div
                  className="tl-viewport"
                  ref={viewportRef}
                  style={{ height: vpHeight }}
                >
                  <div
                    className="tl-canvas"
                    style={{ width: canvasW, height: vpHeight }}
                  >
                    {/** Disabled canvas */}
                    <div
                      className="tl-canvas"
                      style={{ width: canvasW, height: vpHeight }}
                    >
                      {/* Normal disabled (grey stripes) */}
                      {scheduleDisabled.map(({ a, b }, idx) => {
                        const left = (a - startHour * 60) * pxPerMin;
                        const width = Math.max(0, (b - a) * pxPerMin);
                        return (
                          <div
                            key={`dis-sched-${idx}`}
                            style={{
                              position: "absolute",
                              top: HEAD_H,
                              height: tables.length * ROW_H,
                              left,
                              width,
                              background:
                                "repeating-linear-gradient(135deg, rgba(148,163,184,.22), rgba(148,163,184,.22) 8px, rgba(148,163,184,.12) 8px, rgba(148,163,184,.12) 16px)",
                              pointerEvents: "none",
                              borderLeft: "1px solid #cbd5e1",
                              borderRight: "1px solid #cbd5e1",
                              zIndex: 1,
                            }}
                            aria-hidden
                          />
                        );
                      })}

                      {/* Closure disabled (light red stripes) */}
                      {closureDisabled.map(({ a, b }, idx) => {
                        const left = (a - startHour * 60) * pxPerMin;
                        const width = Math.max(0, (b - a) * pxPerMin);
                        return (
                          <div
                            key={`dis-closure-${idx}`}
                            style={{
                              position: "absolute",
                              top: HEAD_H,
                              height: tables.length * ROW_H,
                              left,
                              width,
                              background:
                                "repeating-linear-gradient(135deg, rgba(248,113,113,.22), rgba(248,113,113,.22) 8px, rgba(254,226,226,.14) 8px, rgba(254,226,226,.14) 16px)",
                              pointerEvents: "none",
                              borderLeft: "1px solid #fecaca",
                              borderRight: "1px solid #fecaca",
                              zIndex: 2, // on top of normal disabled
                            }}
                            aria-hidden
                          />
                        );
                      })}

                      {/* …rest of your ticks / rows / events… */}
                    </div>

                    {/* Full-hour ticks (00:00 … 24:00) */}
                    {Array.from({ length: endHour - startHour + 1 }, (_, i) => {
                      const hour = startHour + i;
                      const left = (hour - startHour) * 60 * pxPerMin;
                      return (
                        <div
                          key={`h-${hour}`}
                          className="tl-hour"
                          style={{
                            position: "absolute",
                            top: 0,
                            left,
                            height: "100%",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: HEAD_H,
                              bottom: 0,
                              width: 1,
                              background: "var(--line)",
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              height: HEAD_H,
                              display: "flex",
                              alignItems: "normal",
                              paddingLeft: "13px",
                              paddingTop: "20px",
                              fontSize: ".85rem",
                              color: "var(--muted)",
                              whiteSpace: "nowrap",
                              transform: "rotate(-90deg)",
                              fontWeight: "600",
                            }}
                          >
                            {fmtHourLabelInto24(hour)}
                          </div>
                        </div>
                      );
                    })}

                    {/* Quarter ticks in header (15/30/45 between hours) */}
                    {Array.from(
                      { length: (endHour - startHour) * 4 },
                      (_, i) => {
                        const minsFromStart = (i + 1) * 15; // 15,30,45,... up to before last hour boundary
                        const left = minsFromStart * pxPerMin;
                        const minute = minsFromStart % 60;
                        const label =
                          minute === 15
                            ? ":15"
                            : minute === 30
                              ? ":30"
                              : minute === 45
                                ? ":45"
                                : "";
                        return (
                          <div
                            key={`q-${i}`}
                            style={{
                              position: "absolute",
                              top: 0,
                              left,
                              height: "100%",
                              pointerEvents: "none",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                top: HEAD_H,
                                bottom: 0,
                                width: 1,
                                background: "#eef2f7",
                              }}
                            />
                            {label && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  height: HEAD_H,
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: "10px",
                                  color: "#9aa4af",
                                  transform: "rotate(-90deg) translateX(0)",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {/* {label} */}
                                {minsToTime(minute)}
                              </div>
                            )}
                          </div>
                        );
                      },
                    )}

                    {/* Rows */}
                    {tables.map((t, rowIdx) => {
                      const top = HEAD_H + rowIdx * ROW_H;

                      return (
                        <div
                          key={rowIdx + 1}
                          className="tl-row"
                          style={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            top,
                            height: ROW_H,
                            borderTop: "1px solid var(--line)",
                            cursor: "crosshair",
                          }}
                          onPointerDown={(e) => onRowPointerDown(t?.id, e)}
                        >
                          {/* minute marks every 15 mins in the row */}
                          {Array.from(
                            { length: totalMins / 15 + 1 },
                            (_, k) => {
                              const left = k * 15 * pxPerMin;
                              const major = k % 4 === 0;
                              return (
                                <div
                                  key={k}
                                  style={{
                                    position: "absolute",
                                    top: 1,
                                    bottom: 0,
                                    left,
                                    width: 1,
                                    background: major ? "#e2e8f0" : "#f1f5f9",
                                  }}
                                />
                              );
                            },
                          )}

                          {/* events for this row */}
                          {(() => {
                            const rowEvents = dayEvents.filter(
                              (bk) => bk.tableId === t.id,
                            );
                            const { items, laneCount } = layoutRow(rowEvents);

                            const laneH = (ROW_H - 8) / laneCount; // -8 to keep some padding

                            return items.map((bk) => {
                              const left =
                                (bk.start - startHour * 60) * pxPerMin;
                              const width = Math.max(
                                2,
                                (bk.end - bk.start) * pxPerMin,
                              );
                              const top = 4 + bk.lane * laneH;

                              return (
                                <>
                                  <div
                                    key={bk.id}
                                    className={
                                      updateLoading
                                        ? "ev-chip disabled"
                                        : "ev-chip "
                                    }
                                    style={{
                                      left,
                                      width,
                                      top,
                                      height: laneH - 4,
                                      background: bk.colour || "#3b82f6",
                                      color: bk.textColour || "#fff",
                                      cursor: "pointer",
                                      position: "absolute",
                                      borderRadius: 6,
                                    }}
                                    title={`${bk.name || "Guest"} • ${
                                      bk.chairs
                                    } • ${fmtHM(bk.start)}–${fmtHM(bk.end)}`}
                                    onClick={(e) => {
                                      if (updateLoading) return;
                                      if (
                                        e.target.closest(".ev-menu-btn") ||
                                        e.target.closest(".ev-menu")
                                      )
                                        return;
                                      setEditing(bk);
                                    }}
                                  >
                                    {bk.id == menuFor && updateLoading ? (
                                      <span className="ev-name">
                                        Please wait!
                                      </span>
                                    ) : (
                                      <>
                                        <span className="ev-name">
                                          {bk.name || "Guest"}
                                        </span>
                                        <span className="ev-meta">
                                          {bk.chairs} · {fmtHM(bk.start)}–
                                          {fmtHM(bk.end)}
                                        </span>
                                      </>
                                    )}
                                    {/* --- Kebab button --- */}
                                    {bk.id == menuFor &&
                                    (updateLoading ||
                                      (reservationLoading &&
                                        bk.id === eventId)) ? (
                                      <button
                                        type="button"
                                        className="ev-menu-btn"
                                        disabled
                                      >
                                        <span
                                          className="spinner-border spinner-border-sm"
                                          aria-hidden="true"
                                        ></span>
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="ev-menu-btn"
                                        aria-label="More"
                                        onPointerDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();

                                          if (menuFor === bk.id) {
                                            setMenuFor(null);
                                            return;
                                          }

                                          const r =
                                            e.currentTarget.getBoundingClientRect();
                                          const MENU_W = 200;
                                          const MENU_H = 280;
                                          const pos = pickPlacement(
                                            r,
                                            MENU_W,
                                            MENU_H,
                                            8,
                                            6,
                                          );

                                          setMenuPos({
                                            x: pos.x,
                                            y: pos.y,
                                            placement: pos.name,
                                          });
                                          setMenuFor(bk.id);
                                        }}
                                      >
                                        ⋮
                                      </button>
                                    )}

                                    {/* --- Dropdown --- */}
                                    {menuFor === bk.id && (
                                      <PortalMenu
                                        menuPos={menuPos}
                                        onClose={() => setMenuFor(null)}
                                        items={Object.entries(STATUS_COLOURS)}
                                        updateLoading={updateLoading}
                                        reservationLoading={reservationLoading}
                                        onSelect={(label) => {
                                          const item = dayEvents.find(
                                            (x) => x.id === menuFor,
                                          );
                                          if (!item) return;
                                          handleUpdateStatus(item, label);
                                        }}
                                        onDelete={() => {
                                          const item = dayEvents.find(
                                            (x) => x.id === menuFor,
                                          );
                                          if (!item) return;
                                          setShowConfirm(true);
                                          setEventId(item.id);
                                          window.sessionStorage.setItem(
                                            "eventID",
                                            item.id,
                                          );
                                        }}
                                        selectedDate={selectedDate}
                                      />
                                    )}
                                  </div>
                                </>
                              );
                            });
                          })()}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {editing && (
                <EditorModal
                  show={!!editing}
                  payload={editing}
                  onClose={() => {
                    setEditing(null);
                  }}
                  onSave={saveBooking}
                  onDelete={deleteBooking}
                  fetchDateEvents={fetchDateEvents}
                />
              )}
            </div>
          </div>
          <StatusLegend />
          {/* <div className="d-flex justify-content-between px-4">
          <button
            type="button"
            className="closure_modal_btn"
            disabled={reservationLoading}
            onClick={() => {
              setShowClosureModal(true);
              setIsClosed(false);
            }}
          >
            Would you like to close this date for reservations?
          </button>
        </div> */}
        </div>
        {showClosureModal && (
          <ClosureDrawer
            selectedDate={selectedDate}
            isClosed={isClosed}
            onCancel={oncloseClosureModal}
            selectedClosure={selectedClosure}
            setIsClosed={setIsClosed}
          />
        )}
      </div>
    </>
  );
}
